import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { AuthContext } from '../context/AuthContext'; 
import { loginUser } from '../api/auth'; 
import logo from '../assets/login.png';
import { Mail, Lock, XCircle } from 'lucide-react';
import GuestAuth from '../context/GuestAuth';

export default function Login({ onLogin }) {
  const { setAuth } = useContext(AuthContext); 
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.fromPath;

  const pageTitles = {
    "/dashboard": "Dashboard",
    "/transactions": "Transactions",
    "/assistant": "Assistant",
    "/reports": "Reports",
  };

  const attemptedPage = fromPath && pageTitles[fromPath] 
    ? pageTitles[fromPath] 
    : "this page";


  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (location.state?.fromProtected) {
      setShowPopup(true);
    }
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const data = await loginUser(email, password);

      localStorage.setItem('access_token',  data.access);
      localStorage.setItem('refresh_token', data.refresh);

      setAuth({
        access:   data.access,
        refresh:  data.refresh,
        username: data.username,
        isStaff:  data.isStaff,      
      });

      navigate(data.isStaff ? '/admin' : '/dashboard');
    } catch (error) {
      setError('Invalid email or password');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen font-sans bg-gray-100 px-4">

     {/* === Popup Modal === */}
{showPopup && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative animate-fade-in-up">
      {/* Close Button */}
      <button
        onClick={() => setShowPopup(false)}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
      >
        <XCircle size={24} />
      </button>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#2ecfe3] to-[#b4d1c3] rounded-t-2xl p-6 text-center">
        <h2 className="text-2xl font-bold text-white">Login Required</h2>
      </div>

      {/* Body */}
      <div className="p-6 text-center">
        <p className="text-gray-600 mb-6 leading-relaxed">
        To access <span className="font-semibold">{attemptedPage}</span> you need to create an account. <br />
        Not ready yet? You can{" "}
        <span className="font-semibold text-[#2ecfe3]">continue as guest</span>.
      </p>


        {/* Actions */}
        <div className="flex justify-center gap-4">
          <button
            onClick={() => setShowPopup(false)}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
          >
            Close
          </button>
          <GuestAuth />
        </div>
      </div>
    </div>
  </div>
)}


      {/* === Desktop / Tablet Layout === */}
      <div className="hidden md:flex w-full max-w-5xl shadow-xl rounded-2xl overflow-hidden border-2 border-[#2ecfe3] flex-col md:flex-row">
        {/* Left Panel */}
        <div className="hidden md:flex md:w-1/2 bg-[#b4d1c3] relative items-center justify-center">
          <img src={logo} alt="NeoFinance" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute bg-black bg-opacity-30 inset-0 flex flex-col items-center justify-center text-white p-8">
            <h2 className="text-2xl font-bold mb-2">The future of finance</h2>
            <p className="text-sm text-center max-w-sm">Safely handle your finances online.</p>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-full md:w-1/2 bg-white flex items-center justify-center">
          <div className="w-full max-w-md px-6 py-10 sm:px-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 text-center mb-2">NeoFinance</h1>
            <p className="text-center text-[#2ecfe3] mb-6">Welcome to NeoFinance</p>

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm mb-1 text-gray-600">Email</label>
                <div className="flex items-center border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-[#b4d1c3]">
                  <div className="pl-3 pr-2 border-r border-gray-300 text-gray-400"><Mail size={18} /></div>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-3 pr-4 py-2 focus:outline-none rounded-r-md"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm mb-1 text-gray-600">Password</label>
                <div className="flex items-center border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-[#b4d1c3]">
                  <div className="pl-3 pr-2 border-r border-gray-300 text-gray-400"><Lock size={18} /></div>
                  <input
                    type="password"
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-3 pr-4 py-2 focus:outline-none rounded-r-md"
                  />
                </div>
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <button
                type="submit"
                className="w-full bg-white text-[#1a1a1a] border border-gray-300 py-2 rounded-md hover:bg-[#2ecfe3] transition"
              >
                Sign in
              </button>

              <div className="text-center text-gray-400 text-sm">or</div>

              <GuestAuth />

              <div className="text-center text-sm mt-4 text-gray-500">
                New to NeoFinance? <a href="/register" className="text-blue-500 hover:underline">Create Account</a>
              </div>
            </form>
          </div>
        </div>
      </div>

    {/* Mobile View - form slightly raised */}
<div className="md:hidden flex items-center justify-center w-full h-screen bg-gray-100">
  <div className="w-full max-w-md shadow-xl rounded-2xl overflow-auto border-2 border-[#2ecfe3] bg-white px-6 py-8 sm:px-8 max-h-[90vh] -translate-y-20">
    <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 text-center mb-2">NeoFinance</h1>
    <p className="text-center text-[#2ecfe3] mb-6">Welcome to NeoFinance</p>

    <form onSubmit={handleLogin} className="space-y-5">
      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm mb-1 text-gray-600">Email</label>
        <div className="flex items-center border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-[#b4d1c3]">
          <div className="pl-3 pr-2 border-r border-gray-300 text-gray-400"><Mail size={18} /></div>
          <input
            type="email"
            id="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full pl-3 pr-4 py-2 focus:outline-none rounded-r-md"
          />
        </div>
      </div>

      {/* Password */}
      <div>
        <label htmlFor="password" className="block text-sm mb-1 text-gray-600">Password</label>
        <div className="flex items-center border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-[#b4d1c3]">
          <div className="pl-3 pr-2 border-r border-gray-300 text-gray-400"><Lock size={18} /></div>
          <input
            type="password"
            id="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full pl-3 pr-4 py-2 focus:outline-none rounded-r-md"
          />
        </div>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <button
        type="submit"
        className="w-full bg-white text-[#1a1a1a] border border-gray-300 py-2 rounded-md hover:bg-[#2ecfe3] transition"
      >
        Sign in
      </button>

      <div className="text-center text-gray-400 text-sm">or</div>

      <GuestAuth />

      <div className="text-center text-sm mt-4 text-gray-500">
        New to NeoFinance? <a href="/register" className="text-blue-500 hover:underline">Create Account</a>
      </div>
    </form>
  </div>
</div>

</div>
  );
}